import { Link, router } from '@inertiajs/react'
import { Edit, Trash2 } from 'lucide-react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { LicenseTypeSummary } from '@/types'
import { DocumentUploader } from '@/components/shared/DocumentUploader'
import { formatCents } from '@/lib/utils'
import { FrequencyBadge } from '@/components/shared/FrequencyBadge'

type Template = { id: string; name: string; file_url: string | null; created_at: string }
type Props = {
  type: LicenseTypeSummary
  templates: Template[]
}


export default function LicenseTypesShow({ type, templates }: Props) {
  const handleDelete = () => {
    if (confirm('Are you sure you want to delete this license type?')) {
      router.delete(`/backoffice/license_types/${type.id}`)
    }
  }

  return (
    <BackofficeLayout
      title={type.name}
      breadcrumbs={[
        { label: 'Backoffice', href: '/backoffice/dashboard' },
        { label: 'License Types', href: '/backoffice/license_types' },
        { label: type.name },
      ]}
    >
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-1">
          <h1 className="text-xl font-semibold tracking-tight">{type.name}</h1>
          <Badge variant="secondary" className="font-mono text-[11px]">{type.code}</Badge>
          <FrequencyBadge frequency={type.frequency} />
        </div>
        <p className="text-sm text-muted-foreground">{type.jurisdiction_label}</p>
      </div>

      <div className="flex items-center gap-2 mb-6">
        <Button variant="outline" size="sm" asChild>
          <Link href={`/backoffice/license_types/${type.id}/edit`}>
            <Edit className="w-3.5 h-3.5 mr-1.5" />Edit
          </Link>
        </Button>
        <Button variant="outline" size="sm" onClick={handleDelete} className="text-destructive hover:text-destructive">
          <Trash2 className="w-3.5 h-3.5 mr-1.5" />Delete
        </Button>
      </div>

      <div className="space-y-6 mb-8">
        <section className="border rounded-lg">
          <div className="px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground">Fees</h2>
          </div>
          <div className="px-4 py-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Gov Fee</span>
              <span>{formatCents(type.gov_fee_cents)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Service Fee</span>
              <span>{formatCents(type.service_fee_cents)}</span>
            </div>
            <div className="h-px bg-border" />
            <div className="flex justify-between text-sm font-medium">
              <span>Total</span>
              <span>{formatCents(type.gov_fee_cents + type.service_fee_cents)}</span>
            </div>
          </div>
        </section>

        <section className="border rounded-lg">
          <div className="px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground">Validity & Processing</h2>
          </div>
          <div className="px-4 py-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Validity Period</span>
              <span>{type.validity_period_months ? `${type.validity_period_months} months` : 'Perpetual'}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Processing Time</span>
              <span>
                {type.processing_days_min != null && type.processing_days_max != null
                  ? `${type.processing_days_min} - ${type.processing_days_max} business days`
                  : 'Not specified'}
              </span>
            </div>
          </div>
        </section>
      </div>

      {type.fields.length > 0 && (
        <section className="border rounded-lg mb-8">
          <div className="px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground">Required Fields</h2>
          </div>
          <div className="px-4 py-4">
            <div className="space-y-2">
              {type.fields.map((field) => (
                <div key={field.id} className="flex items-center justify-between text-sm py-1.5 border-b last:border-0 mb-1">
                  <span>{field.label}</span>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-[11px]">{field.field_type}</Badge>
                    {field.required && <Badge className="text-[11px]">Required</Badge>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <DocumentUploader
        title="Document Templates"
        documents={templates}
        uploadPath={`/backoffice/license_types/${type.id}/document_templates`}
        deletePath={(docId) => `/backoffice/license_types/${type.id}/document_templates/${docId}`}
      />
    </BackofficeLayout>
  )
}
